import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Zap, Lock, TrendingUp, Code, 
  ArrowRight, BarChart3, Check, Terminal, 
  Globe, Server, Smartphone, MessageSquare, 
  Image as ImageIcon, Cpu, Layers, X, Copy
} from 'lucide-react';
import toast from 'react-hot-toast';

const HomePage = () => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const companies = [
    { name: 'SocialGuard', handle: '@socialguard' },
    { name: 'SafeStream', handle: '@safestream' },
    { name: 'ChatSecure', handle: '@chatsecure' },
    { name: 'MediaFlow', handle: '@mediaflow' },
    { name: 'ContentAI', handle: '@contentai' },
    { name: 'TrustNet', handle: '@trustnet' },
    { name: 'SafeSpace', handle: '@safespace' },
    { name: 'ModBot', handle: '@modbot' }
  ];

  const codeSnippet = `// Initialize the client
const nsfw = new NSFWClient('YOUR_API_KEY');

// Analyze an image
const result = await nsfw.analyze({
  image: 'https://techycsr.dev/image.jpg',
  models: ['nudity', 'gore']
});

console.log(result.score); // 0.02`;

  const jsonResult = `{
  "id": "req_123abc",
  "status": "success",
  "score": 0.02,
  "flagged": false,
  "categories": {
    "nudity": 0.01,
    "gore": 0.01,
    "violence": 0.00
  },
  "latency": 45
}`;

  const snippets = {
    'Node.js': `const axios = require('axios');

const analyzeImage = async () => {
  try {
    const response = await axios.post('https://nsfwbackend.techycsr.dev/api/analyze', {
      url: 'https://techycsr.dev/image.jpg'
    }, {
      headers: {
        'X-API-Key': 'YOUR_API_KEY'
      }
    });
    
    console.log(response.data);
  } catch (error) {
    console.error(error);
  }
};

analyzeImage();`,
    'Python': `import requests

url = "https://nsfwbackend.techycsr.dev/api/analyze"
headers = {
    "X-API-Key": "YOUR_API_KEY"
}
data = {
    "url": "https://techycsr.dev/image.jpg"
}

response = requests.post(url, json=data, headers=headers)
print(response.json())`,
    'Ruby': `require 'uri'
require 'net/http'
require 'json'

url = URI("https://nsfwbackend.techycsr.dev/api/analyze")

http = Net::HTTP.new(url.host, url.port)
http.use_ssl = true

request = Net::HTTP::Post.new(url)
request["X-API-Key"] = "YOUR_API_KEY"
request["Content-Type"] = "application/json"
request.body = JSON.dump({
  "url": "https://techycsr.dev/image.jpg"
})

response = http.request(request)
puts response.read_body`,
    'Go': `package main

import (
	"bytes"
	"encoding/json"
	"fmt"
	"net/http"
)

func main() {
	url := "https://nsfwbackend.techycsr.dev/api/analyze"
	data := map[string]string{"url": "https://example.com/image.jpg"}
	jsonData, _ := json.Marshal(data)

	req, _ := http.NewRequest("POST", url, bytes.NewBuffer(jsonData))
	req.Header.Set("X-API-Key", "YOUR_API_KEY")
	req.Header.Set("Content-Type", "application/json")

	client := &http.Client{}
	resp, _ := client.Do(req)
	defer resp.Body.Close()

	fmt.Println("Response Status:", resp.Status)
}`,
    'PHP': `<?php

$curl = curl_init();

curl_setopt_array($curl, array(
  CURLOPT_URL => 'https://nsfwbackend.techycsr.dev/api/analyze',
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_ENCODING => '',
  CURLOPT_MAXREDIRS => 10,
  CURLOPT_TIMEOUT => 0,
  CURLOPT_FOLLOWLOCATION => true,
  CURLOPT_HTTP_VERSION => CURL_HTTP_VERSION_1_1,
  CURLOPT_CUSTOMREQUEST => 'POST',
  CURLOPT_POSTFIELDS => json_encode(array('url' => 'https://example.com/image.jpg')),
  CURLOPT_HTTPHEADER => array(
    'X-API-Key: YOUR_API_KEY',
    'Content-Type: application/json'
  ),
));

$response = curl_exec($curl);
curl_close($curl);
echo $response;`,
    'Java': `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;

public class Main {
    public static void main(String[] args) throws Exception {
        HttpClient client = HttpClient.newHttpClient();
        
        String json = "{\"url\":\"https://example.com/image.jpg\"}";
        
        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create("https://nsfwbackend.techycsr.dev/api/analyze"))
            .header("X-API-Key", "YOUR_API_KEY")
            .header("Content-Type", "application/json")
            .POST(HttpRequest.BodyPublishers.ofString(json))
            .build();

        HttpResponse<String> response = client.send(request,
            HttpResponse.BodyHandlers.ofString());

        System.out.println(response.body());
    }
}`,
    'Rust': `use reqwest::Client;
use serde_json::json;

#[tokio::main]
async fn main() -> Result<(), Box<dyn std::error::Error>> {
    let client = Client::new();
    
    let resp = client.post("https://nsfwbackend.techycsr.dev/api/analyze")
        .header("X-API-Key", "YOUR_API_KEY")
        .json(&json!({
            "url": "https://example.com/image.jpg"
        }))
        .send()
        .await?
        .text()
        .await?;

    println!("{}", resp);
    Ok(())
}`,
    'Swift': `import Foundation

let url = URL(string: "https://nsfwbackend.techycsr.dev/api/analyze")!
var request = URLRequest(url: url)
request.httpMethod = "POST"
request.setValue("YOUR_API_KEY", forHTTPHeaderField: "X-API-Key")
request.setValue("application/json", forHTTPHeaderField: "Content-Type")

let body = ["url": "https://example.com/image.jpg"]
request.httpBody = try? JSONSerialization.data(withJSONObject: body)

let task = URLSession.shared.dataTask(with: request) { data, response, error in
    if let data = data {
        if let json = try? JSONSerialization.jsonObject(with: data, options: []) {
            print(json)
        }
    }
}

task.resume()`
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copied to clipboard!');
  };

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-pink-50 to-purple-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.05 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="inline-flex items-center space-x-2 bg-primary-100 dark:bg-primary-900/30 px-4 py-2 rounded-full mb-8 cursor-default"
            >
              <Shield className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                Trusted by 10,000+ developers
              </span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 gradient-text leading-tight">
              Content Moderation
              <br />
              Made Simple
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto px-4">
              Enterprise-grade NSFW detection API powered by advanced AI. 
              Fast, accurate, and reliable content moderation for your applications.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 px-4">
              <Link to="/dashboard" className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto">
                <span>Get Started Free</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/docs" className="btn-outline flex items-center justify-center space-x-2 w-full sm:w-auto">
                <Code className="w-5 h-5" />
                <span>View Documentation</span>
              </Link>
            </div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-2xl mx-auto px-4"
            >
              <motion.div whileHover={{ scale: 1.05 }} className="p-4 rounded-2xl bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm">
                <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">99.9%</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Accuracy</div>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="p-4 rounded-2xl bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm">
                <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">&lt;500ms</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Response Time</div>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="p-4 rounded-2xl bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm">
                <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">24/7</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Support</div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-10 border-y border-gray-100 dark:border-dark-800 bg-white/50 dark:bg-dark-900/50 backdrop-blur-sm overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wider mb-8">
            Trusted by innovative teams worldwide
          </p>
          
          <div className="relative flex overflow-hidden group">
            <div className="flex space-x-16 animate-loop-scroll whitespace-nowrap py-2">
              {[...companies, ...companies].map((company, index) => (
                <div key={index} className="flex items-center gap-2 text-xl font-bold text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer">
                  <Shield className="w-6 h-6" />
                  <span>{company.handle}</span>
                </div>
              ))}
            </div>
            <div className="flex space-x-16 animate-loop-scroll whitespace-nowrap py-2 absolute top-0 left-0" aria-hidden="true">
              {[...companies, ...companies].map((company, index) => (
                <div key={index} className="flex items-center gap-2 text-xl font-bold text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors cursor-pointer">
                  <Shield className="w-6 h-6" />
                  <span>{company.handle}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Preview Section */}
      <section className="py-24 bg-gray-50 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Developer Experience First
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                Integrate powerful content moderation in minutes, not days. Our API is designed 
                to be simple, intuitive, and easy to use with any programming language.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Simple REST API endpoints',
                  'Comprehensive documentation',
                  'SDKs for major languages',
                  'Real-time webhooks'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="p-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/docs" className="btn-primary inline-flex items-center gap-2">
                <Terminal className="w-5 h-5" />
                <span>Read the Docs</span>
              </Link>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl opacity-20 blur-xl"></div>
              <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-800">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs text-gray-400 font-mono">example.js</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-800">
                  <div className="p-4 overflow-x-auto">
                    <pre className="text-sm font-mono text-gray-300">
                      <code>{codeSnippet}</code>
                    </pre>
                  </div>
                  <div className="p-4 bg-gray-900/50 overflow-x-auto">
                    <pre className="text-sm font-mono text-green-400">
                      <code>{jsonResult}</code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-dark-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for Every Platform</h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Versatile content moderation for any industry
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: MessageSquare,
                title: 'Social Platforms',
                description: 'Keep your community safe by automatically filtering inappropriate user-generated content.'
              },
              {
                icon: Smartphone,
                title: 'Dating Apps',
                description: 'Ensure user safety and maintain platform quality by verifying profile photos.'
              },
              {
                icon: Globe,
                title: 'E-commerce',
                description: 'Protect your brand by moderating product reviews and seller uploads.'
              },
              {
                icon: Server,
                title: 'Gaming',
                description: 'Create safe gaming environments by monitoring in-game chat and asset uploads.'
              }
            ].map((useCase, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                className="p-6 rounded-2xl bg-gray-50 dark:bg-dark-900 border border-gray-100 dark:border-dark-800 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-4 text-primary-600 dark:text-primary-400">
                  <useCase.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2">{useCase.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                  {useCase.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-16 bg-gray-50 dark:bg-dark-900 border-y border-gray-200 dark:border-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-8">Integrates with your favorite tools</h2>
          <div className="flex flex-wrap justify-center gap-8 md:gap-12 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            {['Node.js', 'Python', 'Ruby', 'Go', 'PHP', 'Java', 'Rust', 'Swift'].map((lang) => (
              <div 
                key={lang} 
                className="flex flex-col items-center gap-2 group cursor-pointer"
                onClick={() => setSelectedLanguage(lang)}
              >
                <div className="w-12 h-12 rounded-lg bg-white dark:bg-dark-800 shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Code className="w-6 h-6 text-gray-400 group-hover:text-primary-600" />
                </div>
                <span className="text-sm font-medium text-gray-500 group-hover:text-primary-600">{lang}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-dark-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Our API?</h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400">
              Everything you need for comprehensive content moderation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: Zap,
                title: 'Lightning Fast',
                description: 'Process images in under 500ms with our optimized inference pipeline'
              },
              {
                icon: Lock,
                title: 'Secure & Private',
                description: 'End-to-end encryption with no data retention. Your privacy is our priority'
              },
              {
                icon: Shield,
                title: 'Highly Accurate',
                description: '99.9% accuracy with state-of-the-art deep learning models'
              },
              {
                icon: TrendingUp,
                title: 'Scalable',
                description: 'Handle millions of requests per day with auto-scaling infrastructure'
              },
              {
                icon: Code,
                title: 'Developer Friendly',
                description: 'Simple REST API with SDKs for popular programming languages'
              },
              {
                icon: BarChart3,
                title: 'Real-time Analytics',
                description: 'Monitor usage, track performance, and optimize your integration'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-dark-800"
              >
                <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg w-fit mb-4">
                  <feature.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-primary-600 to-pink-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8">
            Join thousands of developers using our API for content moderation
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/sign-up" className="w-full sm:w-auto px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="w-full sm:w-auto px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Code Snippet Modal */}
      <AnimatePresence>
        {selectedLanguage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setSelectedLanguage(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200 dark:border-dark-800"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <Code className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold">{selectedLanguage} Integration</h3>
                </div>
                <button
                  onClick={() => setSelectedLanguage(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Use this code snippet to integrate NSFW detection into your {selectedLanguage} application.
                </p>
                
                <div className="relative group">
                  <div className="absolute right-4 top-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => copyToClipboard(snippets[selectedLanguage])}
                      className="p-2 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                      title="Copy code"
                    >
                      <Copy className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto font-mono text-sm leading-relaxed">
                    <code>{snippets[selectedLanguage]}</code>
                  </pre>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setSelectedLanguage(null)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <Link
                    to="/docs"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
                  >
                    <Terminal className="w-4 h-4" />
                    <span>View Full Docs</span>
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomePage;
